import GalleryEventSelect from "./GalleryEventSelect";
import { useEffect, useMemo, useRef, useState } from "react";
import { FaCalendarCheck, FaImages, FaChevronLeft, FaChevronRight, FaCamera, FaAward } from "react-icons/fa";
import "./GalleryEventDropdown.css";

const galleryEvents = [
  "Internal Hackathon",
  "Excellence Award",
  "Freshers",
  "Saraswati Pujo",
  "Education Excursion",
  "Picnic",
  "Teacher's Day",
  "Achievements",
];

function loadCollection(context) {
  return context.keys().sort().map((path) => ({
    id: path,
    src: context(path),
  }));
}

const eventCollections = {
  "Excellence Award": loadCollection(
    require.context("../../Assets/AWARD SHOW", false, /\.(jpe?g|png|webp)$/i)
  ),
  Picnic: loadCollection(
    require.context("../../Assets/PICNIC", false, /\.(jpe?g|png|webp)$/i)
  ),
};

function GalleryEventDropdown() {
  const photoDialogRef = useRef(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const photoOpen = selectedPhoto !== null;
  const [selectedEvent, setSelectedEvent] = useState(galleryEvents[0]);
  const photos = eventCollections[selectedEvent] || [];
  const photoIndex = selectedPhoto ? photos.findIndex((photo) => photo.src === selectedPhoto.src) : -1;

  function navigatePhoto(direction) {
    const nextIndex = (photoIndex + direction + photos.length) % photos.length;
    setSelectedPhoto({ src: photos[nextIndex].src, alt: `${selectedEvent} photo ${nextIndex + 1}` });
  }

  useEffect(() => {
    if (!photoOpen) return;
    const dialog = photoDialogRef.current;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = "hidden";
    return () => {
      if (dialog.open) dialog.close();
      document.body.style.overflow = previousOverflow;
    };
  }, [photoOpen]);

  const selectedIndex = useMemo(
    () => galleryEvents.findIndex((event) => event === selectedEvent) + 1,
    [selectedEvent]
  );

  return (
    <section className="gallery-event-panel" id="gallery-events">
      <div className="gallery-event-glows" aria-hidden="true">
        <span className="gallery-event-glow gallery-event-glow--blue" />
        <span className="gallery-event-glow gallery-event-glow--teal" />
        <span className="gallery-event-glow gallery-event-glow--violet" />
      </div>
      <div className="gallery-event-copy">
        <span>
          <FaImages />
          Gallery Events
        </span>

        <h2>Choose an event collection</h2>

        <p>
          Select a campus event from the dropdown to browse the matching gallery
          collection and keep photos organized by occasion.
        </p>
        <div className="gallery-event-highlights" aria-label="Campus collections">
          <div className="gallery-event-highlight">
            <div className="gallery-event-highlight-icon"><FaCamera aria-hidden="true" /></div>
            <small>Captured moments</small>
          </div>
          <div className="gallery-event-highlight">
            <div className="gallery-event-highlight-icon"><FaImages aria-hidden="true" /></div>
            <small>Event highlights</small>
          </div>
          <div className="gallery-event-highlight">
            <div className="gallery-event-highlight-icon"><FaAward aria-hidden="true" /></div>
            <small>Campus milestones</small>
          </div>
        </div>
      </div>

      <div className="gallery-event-control">
        <label id="gallery-event-label" htmlFor="gallery-event-select">
          <FaCalendarCheck />
          Events Name
        </label>

        <GalleryEventSelect
          options={galleryEvents}
          value={selectedEvent}
          onChange={(eventName) => {
            setSelectedEvent(eventName);
          }}
        />

        <div className="gallery-event-selected">
          <small>Selected event</small>
          <strong>
            {selectedIndex}. {selectedEvent}
          </strong>
        </div>
      </div>

      <div className="gallery-event-collection" id="gallery-collection" tabIndex={-1} aria-labelledby="gallery-collection-title">
        <h3 id="gallery-collection-title">{selectedEvent} — Photos</h3>
        {photos.length > 0 ? (
          <div className="gallery-event-media-grid" key={selectedEvent}>
            {photos.map((media, index) => (
              <figure className="gallery-event-media" key={media.id}>
                  <button
                    type="button"
                    className="gallery-photo-open"
                    aria-haspopup="dialog"
                    aria-label={`View ${selectedEvent} photo ${index + 1}`}
                    onClick={() => setSelectedPhoto({ src: media.src, alt: `${selectedEvent} photo ${index + 1}` })}
                  >
                    <img src={media.src} alt={`${selectedEvent} photo ${index + 1}`} loading="lazy" />
                  </button>
              </figure>
            ))}
          </div>
        ) : (
          <p className="gallery-event-empty">
            Photos for this event will be added soon.
          </p>
        )}
      </div>
      <dialog
        ref={photoDialogRef}
        className="gallery-photo-dialog"
        aria-labelledby="gallery-photo-dialog-title"
        onCancel={() => setSelectedPhoto(null)}
        onClose={() => setSelectedPhoto(null)}
        onKeyDown={(event) => {
          if (selectedPhoto && photos.length > 1 && ["ArrowLeft", "ArrowRight"].includes(event.key)) {
            event.preventDefault();
            navigatePhoto(event.key === "ArrowRight" ? 1 : -1);
          }
        }}
        onClick={(event) => {
          if (event.target !== event.currentTarget) return;
          const bounds = event.currentTarget.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right ||
              event.clientY < bounds.top || event.clientY > bounds.bottom) {
            setSelectedPhoto(null);
          }
        }}
      >
        <div className="gallery-photo-dialog-heading">
          <h3 id="gallery-photo-dialog-title">{selectedPhoto?.alt}</h3>
          <button type="button" aria-label="Close photo" onClick={() => setSelectedPhoto(null)} autoFocus>
            &times;
          </button>
        </div>
        {selectedPhoto && <img src={selectedPhoto.src} alt={selectedPhoto.alt} />}
        {selectedPhoto && (
          <div className="gallery-photo-navigation">
            <button type="button" aria-label="Previous photo" disabled={photos.length < 2} onClick={() => navigatePhoto(-1)}><FaChevronLeft /></button>
            <span aria-live="polite">{photoIndex + 1} / {photos.length}</span>
            <button type="button" aria-label="Next photo" disabled={photos.length < 2} onClick={() => navigatePhoto(1)}><FaChevronRight /></button>
          </div>
        )}
      </dialog>
    </section>
  );
}

export default GalleryEventDropdown;
